import { Link, Navigate, Outlet, createRoute } from "@tanstack/react-router";
import { useAuth } from "@clerk/clerk-react";
import { useCurrentUser } from "@/lib/me";
import { useMyFacilitatedRoomsQuery } from "@/lib/facilitator/queries";
import { StatusBadge } from "@/components/staff/StatusBadge";
import { SegmentProgress } from "@/components/staff/SegmentProgress";
import { shortDate } from "@/lib/staff/format";
import { UserMenu } from "@/components/UserMenu/UserMenu";
import { useAccessibleSurfaces } from "@/lib/auth/useAccessibleSurfaces";
import { rootRoute } from "./root";
import { NotFound } from "@/components/NotFound";
import styles from "./facilitate.module.scss";

function FacilitateShell() {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoading, isError, data } = useCurrentUser();
  const { surfaces } = useAccessibleSurfaces();

  if (!isLoaded) return <Loading />;
  if (!isSignedIn) return <Navigate to="/sign-in" />;
  if (isLoading) return <Loading />;
  if (isError || !data) return <NotFound />;

  return (
    <div className={styles.shell}>
      <nav className={styles.topNav} aria-label="Facilitator surface">
        <Link to="/facilitate" className={styles.brand}>
          Plotlounge Facilitator
        </Link>
        <div className={styles.navUser}>
          <UserMenu
            currentSurface="facilitator"
            accessibleSurfaces={surfaces}
            placement="topnav"
          />
        </div>
      </nav>
      <Outlet />
    </div>
  );
}

function FacilitateLanding() {
  const {
    data: rooms,
    isLoading,
    isError,
    error,
  } = useMyFacilitatedRoomsQuery();

  // No rooms at all → 404 surface (the user might be eligible but has no
  // active assignment, in which case we don't reveal the surface).
  if (isLoading) return <Loading />;
  if (isError) {
    return (
      <main className={styles.root}>
        <div className={styles.errorBanner} role="alert">
          Failed to load your rooms:{" "}
          {(error as Error)?.message ?? "Unknown error"}
        </div>
      </main>
    );
  }
  if (!rooms || rooms.length === 0) return <NotFound />;

  return (
    <main className={styles.root}>
      <header className={styles.header}>
        <h1 className={styles.title}>Your rooms</h1>
        <p className={styles.subtitle}>
          {rooms.length === 1
            ? "You're facilitating one room."
            : `You're facilitating ${rooms.length} rooms.`}
        </p>
      </header>

      <ul className={styles.roomList}>
        {rooms.map((r) => (
          <li key={r.id} className={styles.roomCard}>
            <Link
              to="/facilitate/$id"
              params={{ id: r.id }}
              className={styles.roomLink}
            >
              <div className={styles.roomHead}>
                <h2 className={styles.roomTitle}>{r.name}</h2>
                <StatusBadge status={r.status} />
              </div>
              <p className={styles.roomBook}>
                {r.book.title} — {r.book.author}
              </p>
              <div className={styles.roomMeta}>
                <SegmentProgress
                  current={r.currentSegment}
                  total={r.totalSegments}
                  highlightCurrent={r.status === "ACTIVE"}
                />
                <span className={styles.roomDates}>
                  {shortDate(r.startDate)} → {shortDate(r.endDate)}
                </span>
                <span className={styles.roomMemberCount}>
                  {r.memberCount} {r.memberCount === 1 ? "reader" : "readers"}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}

function Loading() {
  return (
    <div className={styles.loading} role="status" aria-live="polite">
      Loading…
    </div>
  );
}

export const facilitateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/facilitate",
  component: FacilitateShell,
});

export const facilitateIndexRoute = createRoute({
  getParentRoute: () => facilitateRoute,
  path: "/",
  component: FacilitateLanding,
});
